import React from 'react';
import { cn } from "@/lib/utils";

interface TipContentProps {
  content: string;
  className?: string;
  variant?: "full" | "preview" | "investment" | "investment-detailed";
  highlightMatch?: (text: string, query?: string) => React.ReactNode;
  query?: string;
}

export const TipContent = ({ 
  content, 
  className, 
  variant = "full",
  highlightMatch,
  query 
}: TipContentProps) => {
  console.log('TipContent - Raw content received:', content);
  console.log('TipContent - Content type:', typeof content);
  console.log('TipContent - Variant:', variant);
  
  // Parse JSON content if it's a string containing JSON
  let processedContent = content;
  try {
    const parsed = JSON.parse(content);
    console.log('TipContent - Parsed JSON:', parsed);
    // Handle nested JSON structure where content might be in parsed.content
    processedContent = parsed.content || parsed.text || content;
  } catch {
    // Check if content looks like JSON but failed to parse
    if (content.includes('"title":') && content.includes('"content":')) {
      // Extract content from malformed JSON string using multiple strategies
      let extractedContent = '';
      
      // Strategy 1: Try to extract content between quotes
      const contentMatch = content.match(/"content":\s*"([^"]+)"/);
      if (contentMatch) {
        extractedContent = contentMatch[1];
      } else {
        // Strategy 2: Extract everything after "content": and before the next field or end
        const contentStartMatch = content.match(/"content":\s*"([^"]*(?:"[^"]*"[^"]*)*[^"]*)/);
        if (contentStartMatch) {
          extractedContent = contentStartMatch[1];
        } else {
          // Strategy 3: More aggressive extraction - remove JSON structure completely
          extractedContent = content
            .replace(/^\s*\{\s*"title":\s*"[^"]*",?\s*/i, '')
            .replace(/,?\s*"content":\s*"/i, '')
            .replace(/"\s*\}\s*$/i, '')
            .replace(/",?\s*"[^"]*":\s*"[^"]*"\s*$/i, '');
        }
      }
      
      // Clean up the extracted content
      processedContent = extractedContent
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\t/g, '\t')
        .trim();
      
      // If we still have JSON-like content, try one more cleanup
      if (processedContent.includes('"title":') || processedContent.includes('"content":')) {
        processedContent = content; // Fall back to original if all extractions fail
      }
    } else {
      processedContent = content;
    }
  }
  
  // Convert literal \n characters to actual newlines
  processedContent = processedContent.replace(/\\n/g, '\n');
  
  console.log('TipContent - Processed content:', processedContent);
  console.log('TipContent - Content preview (first 100 chars):', processedContent.substring(0, 100));
  // Handle bold text with **
  const formatBoldText = (text: string) => {
    return text.split(/(\*\*.*?\*\*)/).map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // For preview variant, show limited content
  if (variant === "preview") {
    const previewText = processedContent.length > 150 ? `${processedContent.substring(0, 147)}...` : processedContent;
    const formattedText = highlightMatch && query ? highlightMatch(previewText, query) : formatBoldText(previewText);
    
    return (
      <div className={cn("text-muted-foreground text-sm leading-relaxed", className)}>
        {formattedText}
      </div>
    );
  }

  // Enhanced formatting for investment tips
  if (variant === "investment" || variant === "investment-detailed") {
    const formatInvestmentContent = (text: string) => {
      // Clean up the content and split into meaningful sections
      const cleanText = text.trim();
      
      // Split by double newlines to get paragraphs, but handle single newlines within lists
      const sections = cleanText.split(/\n\s*\n/).filter(section => section.trim());
      
      return sections.map((section, index) => {
        const trimmedSection = section.trim();
        
        // Handle section headers (lines that start and end with **)
        if (trimmedSection.match(/^\*\*.*\*\*$/)) {
          const title = trimmedSection.replace(/\*\*/g, '').trim();
          return (
            <h3 
              key={index} 
              className={cn(
                "font-bold mt-6 mb-3 first:mt-0",
                variant === 'investment-detailed' 
                  ? "text-2xl text-primary bg-gradient-primary bg-clip-text text-transparent" 
                  : "text-lg text-primary"
              )}
            >
              {title}
            </h3>
          );
        }
        
        // Handle numbered lists
        if (trimmedSection.match(/^\d+\./) || (trimmedSection.includes('\n') && trimmedSection.split('\n').some(line => line.match(/^\d+\./)))) {
          const lines = trimmedSection.split('\n').filter(line => line.trim() && line.match(/^\d+\./));
          return (
            <div 
              key={index} 
              className={cn(
                "rounded-xl p-4 border border-border/20",
                variant === 'investment-detailed' ? "bg-gradient-secondary/50 p-6" : "bg-gradient-secondary"
              )}
            >
              <div className={cn(
                "space-y-2",
                variant === 'investment-detailed' ? "space-y-3" : ""
              )}>
                {lines.map((line, lineIndex) => {
                  const numberMatch = line.match(/^(\d+)\.\s*(.*)/);
                  if (!numberMatch) return null;
                  
                  const [, number, content] = numberMatch;
                  const cleanContent = content.replace(/\*\*(.*?)\*\*/g, '$1').trim();
                  if (!cleanContent) return null;
                  
                  return (
                    <div 
                      key={lineIndex} 
                      className={cn(
                        "flex items-start gap-3 text-foreground",
                        variant === 'investment-detailed' ? "text-lg" : "text-sm"
                      )}
                    >
                      <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                        {number}
                      </span>
                      <span className="flex-1">{cleanContent}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        
        // Handle bullet point lists (both - and • style)
        if ((trimmedSection.includes('- ') || trimmedSection.includes('• ')) && 
            trimmedSection.split('\n').some(line => line.trim().startsWith('- ') || line.trim().startsWith('• '))) {
          const lines = trimmedSection.split('\n').filter(line => 
            line.trim().startsWith('- ') || line.trim().startsWith('• ')
          );
          return (
            <div 
              key={index} 
              className={cn(
                "rounded-xl p-4 border border-border/20",
                variant === 'investment-detailed' ? "bg-gradient-secondary/50 p-6" : "bg-gradient-secondary"
              )}
            >
              <div className={cn(
                "space-y-2",
                variant === 'investment-detailed' ? "space-y-3" : ""
              )}>
                {lines.map((line, lineIndex) => {
                  const cleanLine = line.replace(/^[•-]\s*/, '').replace(/\*\*(.*?)\*\*/g, '$1').trim();
                  if (!cleanLine) return null;
                  return (
                    <div 
                      key={lineIndex} 
                      className={cn(
                        "flex items-start gap-3 text-foreground",
                        variant === 'investment-detailed' ? "text-lg" : "text-sm"
                      )}
                    >
                      <span className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2"></span>
                      <span className="flex-1">{cleanLine}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        
        // Handle regular paragraphs with proper line breaks
        const paragraphLines = trimmedSection.split('\n').filter(line => line.trim());
        return (
          <div key={index} className="space-y-3">
            {paragraphLines.map((line, lineIndex) => {
              const trimmedLine = line.trim();
              if (!trimmedLine) return null;
              
              return (
                <p 
                  key={lineIndex} 
                  className={cn(
                    "leading-relaxed text-foreground",
                    variant === 'investment-detailed' ? "text-lg" : "text-sm"
                  )}
                >
                  {trimmedLine.split(/(\*\*.*?\*\*)/).map((part, partIndex) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      const boldText = part.slice(2, -2);
                      return (
                        <strong key={partIndex} className="font-semibold text-primary">
                          {boldText}
                        </strong>
                      );
                    }
                    return part;
                  })}
                </p>
              );
            })}
          </div>
        );
      });
    };

    return (
      <div className={cn("space-y-4", className)}>
        {formatInvestmentContent(processedContent)}
      </div>
    );
  }

  // Enhanced formatting for tech tips and regular tips
  const formatTechContent = (text: string) => {
    const sections = text.split(/\n\s*\n/).filter(section => section.trim());
    
    return sections.map((section, index) => {
      const trimmedSection = section.trim();
      
      // Handle Markdown headers (### Title)
      if (trimmedSection.match(/^###\s+(.+)$/)) {
        const title = trimmedSection.replace(/^###\s+/, '').trim();
        return (
          <h3 key={index} className="text-xl font-bold text-primary mt-6 mb-3 first:mt-0">
            {title}
          </h3>
        );
      }
      
      // Handle Week headers (Week 1:, Week 2:, etc.) and Milestone headers
      if (trimmedSection.match(/^(Week \d+:|Milestone:)/)) {
        return (
          <h3 key={index} className="text-lg font-bold text-primary mt-6 mb-3 first:mt-0">
            {trimmedSection}
          </h3>
        );
      }
      
      // Handle numbered lists - detect the actual pattern from the content
      const hasNumberedList = trimmedSection.includes('\n') && 
        trimmedSection.split('\n').some(line => line.trim().match(/^\d+\.\s+\*\*/));
      
      if (hasNumberedList) {
        const lines = trimmedSection.split('\n').filter(line => line.trim().match(/^\d+\.\s+\*\*/));
        return (
          <div key={index} className="bg-gradient-secondary rounded-lg p-4 border border-border/20 mb-4">
            <ol className="space-y-3">
              {lines.map((line, lineIndex) => {
                const numberMatch = line.match(/^(\d+)\.\s+\*\*(.*?)\*\*:\s*(.*)/);
                if (!numberMatch) return null;
                
                const [, number, title, description] = numberMatch;
                
                return (
                  <li key={lineIndex} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                      {number}
                    </span>
                    <div className="flex-1">
                      <strong className="text-primary font-semibold">{title}:</strong>
                      <span className="ml-1 text-foreground">{description.trim()}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        );
      }
      
      // Handle bullet points (• or -)
      if ((trimmedSection.includes('•') || trimmedSection.includes('- ')) && 
          trimmedSection.split('\n').some(line => line.trim().startsWith('•') || line.trim().startsWith('- '))) {
        const bullets = trimmedSection.split('\n').filter(line => 
          line.trim().startsWith('•') || line.trim().startsWith('- ')
        );
        
        return (
          <div key={index} className="space-y-3">
            <ul className="space-y-2 ml-4">
              {bullets.map((bullet, i) => (
                <li key={`bullet-${i}`} className="flex items-start">
                  <span className="text-primary mr-3 mt-1 flex-shrink-0">•</span>
                  <span className="text-foreground">
                    {highlightMatch && query 
                      ? highlightMatch(bullet.replace(/^[•-]\s*/, '').trim(), query)
                      : formatBoldText(bullet.replace(/^[•-]\s*/, '').trim())
                    }
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      }

      // Regular paragraphs - handle bold text properly
      const paragraphLines = trimmedSection.split('\n').filter(line => line.trim());
      if (paragraphLines.length === 1) {
        return (
          <p key={index} className="leading-relaxed text-foreground mb-4">
            {highlightMatch && query ? highlightMatch(paragraphLines[0], query) : formatBoldText(paragraphLines[0])}
          </p>
        );
      }
      
      return (
        <div key={index} className="space-y-2 mb-4">
          {paragraphLines.map((line, lineIndex) => (
            <p key={lineIndex} className="leading-relaxed text-foreground">
              {highlightMatch && query ? highlightMatch(line, query) : formatBoldText(line)}
            </p>
          ))}
        </div>
      );
    });
  };

  return (
    <div className={cn("space-y-4", className)}>
      {formatTechContent(processedContent)}
    </div>
  );
};